import { enforceConfiguration, DEFAULT_PROCTORING_CONFIGURATION_OPTIONS } from "@verity/proctoring-option";
import { ConfigurationPageName, PendingLiveSession, ProctoringOptionCategory, VerityInstituteSettings } from "../..";
import { UseEditQuizNavigationReturn } from "../hooks/useEditQuizNavigation";
import { ProctoringDataType } from "../hooks/state/useSettingsState";
import { OptionRelatedInstituteSettings, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { EditQuizPageProps } from "../editQuiz";
import { AccommodationBlock } from "./blocks/AccommodationBlocks";
import {
	MethodBlock,
	LiveSessionBlock,
	ProfileBlock,
	ConfigurationBlock,
	ConfigurationBlockProps,
	PlatformBlock,
	PlatformBlockProps,
} from "./blocks/ProctoringBlocks";
import { useCallback, useMemo } from "react";
import styles from "./index.module.css";
import { ThirdPartyPasswordBlock, ThirdPartyPlatformBlock, ThirdPartyQuizNameBlock } from "./blocks/ThirdPartyBlocks";

export function ConfirmationSummaryRow({ children }: { children: React.ReactNode }) {
	return <div className={styles.compactTopRow}>{children}</div>;
}

export type EditQuizSummaryProps = {
	pendingLiveSessions?: PendingLiveSession[];
	loading?: boolean;
	handleGoToStep?: UseEditQuizNavigationReturn["handleGoToStep"];
	verityInstituteSettings?: VerityInstituteSettings;
	activeProfile?: ProctoringOptionProfileV2;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	allowedCategories?: ProctoringOptionCategory[];
	enforcedProfilesOnly?: boolean;
	platformOptions?: PlatformBlockProps["platformOptions"];
	visibleBlocks?: ConfigurationPageName[];
	thirdPartyPlatforms?: ProctoringDataType["thirdPartyPlatforms"];
	/**Pass this in to see Accommodation Step */
	onManageAccommodations?: () => void;
	numAccommodated?: number;
} & Pick<EditQuizPageProps, "proctoringOption" | "proctoringAssessmentConfiguration" | "quizName" | "quizType"> &
	Pick<ProctoringDataType, "applications" | "extensions"> &
	Pick<ConfigurationBlockProps, "referenceMaterialFiles">;

/**The core confirmation blocks. Third Party, Method, Profile, Platform, Configuration, Accommodation */
export default function EditQuizSummary(props: EditQuizSummaryProps) {
	const { loading, handleGoToStep, enforcedProfilesOnly } = props;

	const createEditHandler = useCallback(
		(step: Parameters<NonNullable<typeof handleGoToStep>>[0]) => {
			if (!handleGoToStep) return undefined;
			return () => handleGoToStep(step);
		},
		[handleGoToStep],
	);

	const isBlockVisible = useCallback(
		(blockName: ConfigurationPageName) => {
			if (!props.visibleBlocks) return true; // Default to showing everything
			return props.visibleBlocks.includes(blockName);
		},
		[props.visibleBlocks],
	);

	const topRowBlocks = useMemo(() => {
		const blocks: React.ReactNode[] = [];

		if (!props.verityInstituteSettings?.liteMode && isBlockVisible("method")) {
			blocks.push(
				<MethodBlock
					key="method"
					loading={loading}
					onEdit={enforcedProfilesOnly ? undefined : createEditHandler("method")}
					proctoringAssessmentConfiguration={props.proctoringAssessmentConfiguration}
				/>,
			);
		}

		if (props.proctoringAssessmentConfiguration?.isLiveProctored && isBlockVisible("liveSessions")) {
			blocks.push(
				<LiveSessionBlock
					key="liveSessions"
					loading={loading}
					onEdit={createEditHandler("liveSessions")}
					pendingLiveSessions={props.pendingLiveSessions ?? []}
				/>,
			);
		}

		if (props.activeProfile && isBlockVisible("profile")) {
			blocks.push(
				<ProfileBlock
					key="profile"
					loading={loading}
					onEdit={createEditHandler("profile")}
					profile={props.activeProfile}
				/>,
			);
		}

		if (isBlockVisible("platform")) {
			blocks.push(
				<PlatformBlock
					key="platform"
					loading={loading}
					onEdit={
						enforcedProfilesOnly || props.activeProfile?.type === "preset" ? undefined : createEditHandler("platform")
					}
					platformOptions={
						props.platformOptions ??
						(props.proctoringOption
							? enforceConfiguration(
									DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
									props.proctoringOption,
									props.verityInstituteSettings,
									props.optionRelatedInstituteSettings,
								).proctoringOptions
							: undefined)
					}
					optionRelatedInstituteSettings={props.optionRelatedInstituteSettings}
				/>,
			);
		}

		return blocks;
	}, [
		props.verityInstituteSettings,
		props.proctoringAssessmentConfiguration,
		props.activeProfile,
		props.pendingLiveSessions,
		props.platformOptions,
		props.proctoringOption,
		props.optionRelatedInstituteSettings,
		isBlockVisible,
		loading,
		enforcedProfilesOnly,
		createEditHandler,
	]);

	const thirdPartyBlocks = useMemo(() => {
		const blocks: React.ReactNode[] = [];

		if (props.proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && isBlockVisible("thirdParty")) {
			if (props.quizName) {
				blocks.push(<ThirdPartyQuizNameBlock key="thirdPartyQuizName" loading={loading} quizName={props.quizName} />);
			}

			if (props.thirdPartyPlatforms) {
				blocks.push(
					<ThirdPartyPlatformBlock
						key="thirdPartyPlatform"
						loading={loading}
						thirdPartyOption={props.proctoringAssessmentConfiguration.thirdPartyAssessmentOption}
						thirdPartyPlatforms={props.thirdPartyPlatforms}
					/>,
				);
			}

			blocks.push(
				<ThirdPartyPasswordBlock
					key="thirdPartyPassword"
					loading={loading}
					onEdit={createEditHandler("thirdParty")}
					thirdPartyOption={props.proctoringAssessmentConfiguration.thirdPartyAssessmentOption}
				/>,
			);
		}

		return blocks;
	}, [
		props.proctoringAssessmentConfiguration?.thirdPartyAssessmentOption,
		props.thirdPartyPlatforms,
		props.quizName,
		isBlockVisible,
		loading,
		createEditHandler,
	]);

	const stackedBlocks = useMemo(() => {
		const blocks: React.ReactNode[] = [];
		if (isBlockVisible("configuration")) {
			blocks.push(
				<ConfigurationBlock
					key="configuration"
					loading={loading}
					option={props.proctoringOption}
					verityInstituteSettings={props.verityInstituteSettings}
					onEdit={
						enforcedProfilesOnly || props.activeProfile?.type === "preset" || !handleGoToStep
							? undefined
							: (category) => handleGoToStep(category)
					}
					applications={props.applications}
					extensions={props.extensions}
					optionRelatedInstituteSettings={props.optionRelatedInstituteSettings}
					allowedCategories={props.allowedCategories}
					quizType={props.quizType}
					isThirdPartyEnabled={!!props.proctoringAssessmentConfiguration?.thirdPartyAssessmentOption}
					referenceMaterialFiles={props.referenceMaterialFiles}
				/>,
			);
		}

		if (props.onManageAccommodations) {
			blocks.push(
				<AccommodationBlock
					key="accommodations"
					loading={loading}
					onEdit={props.onManageAccommodations}
					numAccommodated={props.numAccommodated}
				/>,
			);
		}

		return blocks;
	}, [
		isBlockVisible,
		props.onManageAccommodations,
		props.proctoringOption,
		props.verityInstituteSettings,
		props.activeProfile?.type,
		props.applications,
		props.extensions,
		props.optionRelatedInstituteSettings,
		props.allowedCategories,
		props.numAccommodated,
		props.referenceMaterialFiles,
		loading,
		enforcedProfilesOnly,
		handleGoToStep,
	]);

	return (
		<div className={styles.compactContainerLayout}>
			{thirdPartyBlocks.length > 0 && <ConfirmationSummaryRow>{thirdPartyBlocks}</ConfirmationSummaryRow>}
			{topRowBlocks.length > 0 && <ConfirmationSummaryRow>{topRowBlocks}</ConfirmationSummaryRow>}
			{stackedBlocks.length > 0 && stackedBlocks}
		</div>
	);
}
